import api from '@/api/api';

const DOMAIN = '/clubs';

const get = async (
	params?: Clubs.ClubParams,
): Promise<Clubs.GetClubsResponse> => {
	const { data } = await api.get(`${DOMAIN}`, { params });
	return data;
};

const create = async (body: Clubs.Create): Promise<Clubs.Club> => {
	const { data } = await api.post(`${DOMAIN}`, body);

	return data;
};

const patch = async (
	id: string,
	body: Partial<Clubs.Create>,
): Promise<Clubs.Club> => {
	const { data } = await api.patch(`${DOMAIN}/${id}`, body);

	return data;
};

const remove = async (
	id: string,
	body: Partial<Clubs.Create>,
): Promise<Clubs.Club> => {
	const { data } = await api.delete(`${DOMAIN}/${id}`, { data: body });

	return data;
};

const createBenefit = async (
	body: Clubs.CreateBenefit,
): Promise<Clubs.Benefit> => {
	const { data } = await api.post(`${DOMAIN}/benefits`, body);
	return data;
};

const removeBenefit = async (id: string): Promise<void> => {
	await api.delete(`${DOMAIN}/benefits/${id}`);
};

const updateBenefit = async (
	id: string,
	body: Clubs.UpdateBenefit,
): Promise<Clubs.Benefit> => {
	const { data } = await api.put(`${DOMAIN}/benefits/${id}`, body);
	return data;
};

/** Resposta de POST /clubs/assign/:clubId/:userId (admin). */
export type AssignClubToUserResult =
	| { url: string }
	| { activated: boolean; status: string; message?: string };

const assign = async (
	clubId: string,
	userId: string,
	body: { return_url: string },
): Promise<AssignClubToUserResult> => {
	const { data } = await api.post(
		`${DOMAIN}/assign/${clubId}/${userId}`,
		body,
	);
	return data;
};

const cancelUserSubscription = async (
	subscriptionId: string,
): Promise<{ message: string }> => {
	const { data } = await api.delete(`${DOMAIN}/users/${subscriptionId}`);
	return data;
};

const reconcileSubscription = async (
	clubId: string,
	userId: string,
	body?: { stripe_subscription_id?: string },
): Promise<unknown> => {
	const { data } = await api.post(
		`${DOMAIN}/reconcile/${clubId}/${userId}`,
		body ?? {},
	);
	return data;
};

const join = async (
	clubId: string,
	body: { success_url: string; cancel_url: string },
): Promise<string> => {
	const { data } = await api.post(`${DOMAIN}/join/${clubId}`, body);

	return data.url;
};

const getMe = async (): Promise<any> => {
	const { data } = await api.get(`${DOMAIN}/me`);

	return data;
};

const getAvailable = async (): Promise<Clubs.Club[]> => {
	const { data } = await api.get(`${DOMAIN}/available`);

	return data;
};

const getUserBenefitsBalance = async (
	userId: string,
	params?: Clubs.GetUserBenefitsBalanceParams,
): Promise<Clubs.UserBenefitsBalanceResponse> => {
	const { data } = await api.get(
		`${DOMAIN}/users/${userId}/benefits/balance`,
		{
			params,
		},
	);
	return data;
};

const getUserClubHistory = async (
	userId: string,
	params?: Clubs.GetUserClubHistoryParams,
): Promise<Clubs.PaginatedClubTimelineResponse> => {
	const { data } = await api.get(`${DOMAIN}/users/${userId}/history`, {
		params,
	});
	return data;
};

const patchUserBenefit = async (
	userId: string,
	body: Clubs.UpdateUserBenefitUsageBody,
): Promise<Clubs.UserClubSubscriptionBenefit> => {
	const { data } = await api.patch(`${DOMAIN}/benefits/user/${userId}`, body);
	return data;
};

const ClubService = {
	get,
	create,
	patch,
	remove,
	createBenefit,
	removeBenefit,
	updateBenefit,
	assign,
	cancelUserSubscription,
	reconcileSubscription,
	join,
	getMe,
	getAvailable,
	getUserBenefitsBalance,
	getUserClubHistory,
	patchUserBenefit,
};

export { ClubService };
